import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Loader2, RotateCcw, Send } from "lucide-react";
import { toast } from "sonner";
import SEO from "@/components/SEO";
import Markdown from "@/components/Markdown";
import LeadCapture from "@/components/LeadCapture";
import { growth, streamMessage, formatError } from "@/lib/growthApi";
import { START_OPTIONS, SUGGESTED_PROMPTS, CALENDLY_URL } from "@/content";

const KEY = "thrive-adviser-session";

export default function Adviser() {
  const [params] = useSearchParams();
  const startParam = params.get("start");
  const [start, setStart] = useState(startParam || null);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [assessed, setAssessed] = useState(false);
  const [showLead, setShowLead] = useState(false);
  const endRef = useRef(null);
  const abortRef = useRef(null);

  useEffect(() => {
    const saved = !startParam && sessionStorage.getItem(KEY);
    if (saved) {
      growth.session(saved).then((d) => {
        setSessionId(d.id); setStart(d.start_option); setMessages(d.messages);
        setAssessed(d.messages.some((m) => m.content.includes("## What We Understand")));
      }).catch(() => sessionStorage.removeItem(KEY));
    }
  }, [startParam]);

  useEffect(() => {
    if (start && !sessionId) begin(start);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [start]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }); }, [messages, showLead]);

  const begin = async (opt) => {
    try {
      const d = await growth.start(opt);
      setSessionId(d.session_id); setMessages(d.messages); setStart(d.start_option); setAssessed(false); setShowLead(false);
      sessionStorage.setItem(KEY, d.session_id);
    } catch (e) { toast.error(formatError(e)); }
  };

  const reset = () => { abortRef.current?.abort(); abortRef.current = null; sessionStorage.removeItem(KEY); setSessionId(null); setMessages([]); setStart(null); setAssessed(false); setShowLead(false); };

  const send = async (text) => {
    const t = (text || input).trim();
    if (!t || busy || !sessionId) return;
    setInput(""); setBusy(true);
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setMessages((m) => [...m, { role: "user", content: t }, { role: "assistant", content: "" }]);
    try {
      await streamMessage(sessionId, t,
        (tok) => { if (ctrl.signal.aborted) return; setMessages((m) => { if (!m.length) return m; const c = [...m]; c[c.length - 1] = { ...c[c.length - 1], content: c[c.length - 1].content + tok }; return c; }); },
        (ev) => { if (!ctrl.signal.aborted && ev.assessment) setAssessed(true); }, ctrl.signal);
    } catch (e) { if (e?.name !== "AbortError") toast.error("The adviser is unavailable right now. Please try again shortly."); }
    finally { setBusy(false); }
  };

  const startLabel = START_OPTIONS.find(([id]) => id === start)?.[1] || start;

  return (
    <div data-testid="adviser-page" className="pt-28 sm:pt-32">
      <SEO title="AI Growth Adviser | Thrive Business Development" description="A commercial discovery conversation with the Thrive Growth Adviser: understand your market, clarify the opportunity and see where to focus." />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="grid gap-10 lg:grid-cols-[340px_1fr] lg:gap-16">
          <aside className="hidden lg:block">
            <div className="sticky top-32 space-y-10">
              <div>
                <p className="eyebrow mb-5">Thrive Growth Adviser</p>
                <h1 className="font-display text-2xl font-bold tracking-[-0.03em] leading-tight text-white">A discovery conversation, not a form.</h1>
                <p className="mt-4 text-sm text-slate-400 leading-relaxed">Describe what your business does and where you want to grow. The adviser will ask the questions a senior BD director would ask, then set out an initial view of the opportunity.</p>
              </div>
              {start && <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500">Starting point <span className="text-gold">{startLabel}</span></p>}
              <div>
                <p className="mb-3 font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500">Suggested starting points</p>
                <div className="flex flex-col items-start gap-2">
                  {SUGGESTED_PROMPTS.map((p) => (
                    <button key={p} type="button" onClick={() => (sessionId ? send(p) : begin("other").then(() => send(p)))} data-testid="adviser-suggestion" className="text-left text-sm text-slate-300 transition-colors hover:text-gold">{p}</button>
                  ))}
                </div>
              </div>
              <div className="flex flex-col items-start gap-3 border-t border-white/10 pt-6">
                <button type="button" onClick={reset} data-testid="adviser-restart-button" className="inline-flex items-center gap-2 text-sm text-slate-400 transition-colors hover:text-gold"><RotateCcw size={13} /> Start a new assessment</button>
                <Link to="/growth/builder" data-testid="adviser-builder-link" className="inline-flex items-center gap-2 text-sm text-slate-400 transition-colors hover:text-gold"><ArrowRight size={13} /> Opportunity Builder</Link>
                <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" data-testid="adviser-talk-link" className="inline-flex items-center gap-2 text-sm text-slate-400 transition-colors hover:text-gold"><ArrowUpRight size={13} /> Talk to Thrive</a>
              </div>
            </div>
          </aside>

          <div>
            {!start ? (
              <div data-testid="adviser-start">
                <p className="eyebrow mb-5">Where would you like to start?</p>
                <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-[-0.03em] text-white">Choose a starting point.</h2>
                <div className="mt-8 grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2">
                  {START_OPTIONS.map(([id, label]) => (
                    <button key={id} type="button" onClick={() => setStart(id)} data-testid={`adviser-start-${id}`} className="group flex items-center justify-between gap-4 bg-navy p-6 text-left transition-colors hover:bg-navy-light/60">
                      <span className="font-display text-base font-bold tracking-[-0.02em] text-white">{label}</span>
                      <ArrowRight size={16} className="shrink-0 text-slate-600 transition-all group-hover:translate-x-1 group-hover:text-gold" />
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="flex flex-col border border-white/10 bg-navy-light/30" data-testid="adviser-chat">
                <div className="flex items-center justify-between border-b border-white/10 px-5 py-4">
                  <div className="flex items-center gap-3"><span className="h-2 w-2 rounded-full bg-gold" aria-hidden="true" /><span className="font-mono text-[11px] uppercase tracking-[0.25em] text-slate-400">Thrive Growth Adviser</span></div>
                  <button type="button" onClick={reset} data-testid="adviser-restart-inline" className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500 transition-colors hover:text-gold lg:hidden"><RotateCcw size={12} /> Restart</button>
                </div>
                <div className="max-h-[62vh] min-h-[380px] space-y-6 overflow-y-auto px-5 py-6 sm:px-8" data-testid="adviser-messages">
                  {messages.map((m, i) => (
                    <div key={i} className={m.role === "user" ? "flex justify-end" : ""} data-testid={`adviser-message-${m.role}`}>
                      {m.role === "user" ? (
                        <p className="max-w-[85%] border border-gold/30 bg-gold/5 px-5 py-3.5 text-sm text-slate-100 leading-relaxed">{m.content}</p>
                      ) : (
                        <div className="max-w-[92%] text-sm text-slate-300">
                          {m.content ? <Markdown text={m.content} /> : <Loader2 size={15} className="animate-spin text-gold" />}
                        </div>
                      )}
                    </div>
                  ))}
                  {assessed && !showLead && !busy && (
                    <div className="flex flex-wrap gap-3 border-t border-white/10 pt-6" data-testid="adviser-assessment-actions">
                      <button type="button" onClick={() => setShowLead(true)} data-testid="adviser-closer-look-button" className="group inline-flex items-center gap-2 bg-gold px-6 py-3.5 text-[12px] font-semibold uppercase tracking-[0.18em] text-navy transition-all hover:bg-gold-glow">Yes, take a closer look <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" /></button>
                      <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" data-testid="adviser-book-call-button" className="inline-flex items-center gap-2 border border-white/20 px-6 py-3.5 text-[12px] font-semibold uppercase tracking-[0.18em] text-white transition-colors hover:border-gold hover:text-gold">Talk to Thrive <ArrowUpRight size={14} /></a>
                    </div>
                  )}
                  {showLead && <LeadCapture sessionId={sessionId} />}
                  <div ref={endRef} />
                </div>
                {messages.length <= 1 && (
                  <div className="flex flex-wrap gap-2 border-t border-white/10 px-5 py-4 sm:px-8">
                    {SUGGESTED_PROMPTS.slice(0, 4).map((p) => (
                      <button key={p} type="button" onClick={() => send(p)} data-testid="adviser-prompt-chip" className="border border-white/15 px-3 py-2 text-xs text-slate-300 transition-colors hover:border-gold/60 hover:text-gold">{p}</button>
                    ))}
                  </div>
                )}
                <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-end gap-3 border-t border-white/10 px-5 py-4 sm:px-8">
                  <textarea
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                    rows={2}
                    placeholder="Tell the adviser about your business…"
                    data-testid="adviser-input"
                    className="flex-1 resize-none bg-transparent text-sm text-white outline-none placeholder:text-slate-600"
                  />
                  <button type="submit" disabled={busy || !input.trim()} data-testid="adviser-send-button" aria-label="Send" className="flex h-11 w-11 shrink-0 items-center justify-center bg-gold text-navy transition-all hover:bg-gold-glow disabled:opacity-40">
                    {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                  </button>
                </form>
              </div>
            )}
            <p className="mt-5 font-mono text-[10px] leading-relaxed tracking-wider text-slate-600">The adviser offers an initial commercial view based only on what you share. It does not replace current market and account intelligence. No personal details are stored unless you choose to share them.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
